import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShellLayout from '../components/AppShellLayout';
import { IconArrowRight, IconRefresh } from '../components/Icons';
import { capture } from '../analytics';

interface DashboardData {
  connected: boolean;
  provider: 'gmail' | 'microsoft' | null;
  emailCount: number;
  isIngesting: boolean;
  backfillCompleted: boolean;
  keySenderCount: number;
  subscriptionCount: number;
}

interface ClassifyResult {
  classified: number;
  total: number;
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastScanResult, setLastScanResult] = useState<ClassifyResult | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const trialFiredRef = useRef(false);

  useEffect(() => { loadDashboard(); }, []);

  // Fire trial_started once when redirected back after trial activation
  useEffect(() => {
    if (trialFiredRef.current) return;
    const billing = searchParams.get('billing');
    if (billing === 'trial-started' || billing === 'success') {
      trialFiredRef.current = true;
      capture('trial_started');
    }
  }, [searchParams]);

  // Poll while backfill is running
  useEffect(() => {
    if (!data?.connected || data.backfillCompleted) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/dashboard');
        if (res.ok) setData(await res.json());
      } catch {
        // keep polling
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [data?.connected, data?.backfillCompleted]);

  async function loadDashboard() {
    setLoadingStatus(true);
    setLoadError(false);
    try {
      const res = await fetch('/api/dashboard');
      if (!res.ok) { setLoadError(true); return; }
      setData(await res.json());
    } catch {
      setLoadError(true);
    } finally {
      setLoadingStatus(false);
    }
  }

  async function runScan() {
    setScanning(true);
    setScanError(null);
    setLastScanResult(null);
    capture('inbox_scan_started');
    try {
      const res = await fetch('/api/classify', { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result: ClassifyResult = await res.json();
      setLastScanResult(result);
      capture('inbox_scan_completed', { classified: result.classified, total: result.total });
      const refreshed = await fetch('/api/dashboard');
      if (refreshed.ok) setData(await refreshed.json());
    } catch {
      setScanError(t('dashboard.scanFailed'));
    } finally {
      setScanning(false);
    }
  }

  function getBackfillBanner(): string | null {
    if (!data?.connected || data.backfillCompleted) return null;
    if (data.emailCount === 0) return t('dashboard.backfillConnecting');
    return t('dashboard.backfillProgress', { count: data.emailCount.toLocaleString() });
  }

  const backfillBanner = getBackfillBanner();

  return (
    <AppShellLayout>
      <div className="page-header">
        <h1>{t('dashboard.title')}</h1>
        <p>{t('dashboard.subtitle')}</p>
      </div>

      {loadingStatus ? (
        <div className="loading-state"><div className="spinner" aria-label="Loading" /></div>
      ) : loadError ? (
        <p className="state-message state-error">
          {t('dashboard.loadError')}{' '}
          <button className="link-btn" onClick={loadDashboard}>{t('dashboard.retry')}</button>
        </p>
      ) : (
        <>
          {backfillBanner && (
            <div className="info-banner" style={{ marginBottom: '16px' }}>
              <div className="spinner" aria-label="Loading" style={{ width: '14px', height: '14px' }} />
              <span>{backfillBanner}</span>
            </div>
          )}

          <div className="dashboard-grid">
            <div className="dashboard-card">
              <div className="card-label">
                {data?.provider === 'microsoft' ? t('dashboard.microsoft') : t('dashboard.gmail')}
              </div>
              {data?.connected ? (
                <div className="card-status-ok">{t('dashboard.connected')}</div>
              ) : (
                <>
                  <div className="card-status-warn">{t('dashboard.notConnected')}</div>
                  <Link to="/app/connect" className="card-action">
                    {t('dashboard.connectInbox')} <IconArrowRight size={12} />
                  </Link>
                </>
              )}
            </div>

            <div className="dashboard-card">
              <div className="card-label">{t('dashboard.emailsScanned')}</div>
              <div className="card-value">{(data?.emailCount ?? 0).toLocaleString()}</div>
            </div>

            <div className="dashboard-card">
              <div className="card-label">{t('dashboard.subscriptions')}</div>
              <div className="card-value">{(data?.subscriptionCount ?? 0).toLocaleString()}</div>
              {(data?.subscriptionCount ?? 0) > 0 && (
                <Link to="/app/subscriptions" className="card-action">
                  {t('dashboard.viewAll')} <IconArrowRight size={12} />
                </Link>
              )}
            </div>

            {(data?.keySenderCount ?? 0) > 0 && (
              <div className="dashboard-card">
                <div className="card-label">{t('dashboard.keySenders')}</div>
                <div className="card-value">{data!.keySenderCount.toLocaleString()}</div>
                <div className="card-subtitle">{t('dashboard.keySendersSubtitle')}</div>
                <Link to="/app/subscriptions?filter=important" className="card-action">
                  {t('dashboard.view')} <IconArrowRight size={12} />
                </Link>
              </div>
            )}
          </div>

          {data?.connected && data.backfillCompleted && (
            <div className="scan-section">
              <button className="btn-primary" onClick={runScan} disabled={scanning}>
                <IconRefresh size={15} />
                {scanning ? t('dashboard.scanning') : lastScanResult ? t('dashboard.rescanInbox') : t('dashboard.scanInbox')}
              </button>
              {lastScanResult && (
                <span className="scan-result">
                  {t('dashboard.scanComplete', { count: lastScanResult.classified })}
                </span>
              )}
              {scanError && <span className="state-error">{scanError}</span>}
            </div>
          )}
        </>
      )}
    </AppShellLayout>
  );
}
